import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Link from '@mui/material/Link';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import { visuallyHidden } from '@mui/utils';
import { ReactComponent as TriangleIcon } from 'assets/images/Triangle.svg';
import SvgIcon from '@mui/material/SvgIcon';
import Stack from '@mui/material/Stack';

import { Button, CircularProgress, Collapse } from '@mui/material';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { FlatPaginationComp } from 'shared/components/Pagination';
import { useMeasure } from 'react-use';
import { downloadCSV, downloadPDF } from 'shared/helpers/DirectDownload';
import { Toast, Utils } from 'shared/helpers';
import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { ManagePacketService } from 'services';
import { AxiosError } from 'axios';

export const HeaderContext = React.createContext<headerStyle | null>(null);

const headCells: readonly HeadCell[] = [
    {
        id: 'uploadSessionId',
        numeric: false,
        disablePadding: true,
        label: 'Upload session ID',
    },

    {
        id: 'uploadStatus',
        numeric: true,
        disablePadding: false,
        label: 'Upload Status',
    },
    {
        id: 'clientName',
        numeric: true,
        disablePadding: false,
        label: 'Checklist name',
    },
    {
        id: 'bin',
        numeric: true,
        disablePadding: false,
        label: 'Reference Number',
    },
    {
        id: 'downloadLink',
        numeric: true,
        disablePadding: false,
        label: 'Download Audit',
    },
    {
        id: 'packetsData',
        numeric: true,
        disablePadding: false,
        label: 'Cross check information',
    },
];

const headChildCells: readonly HeadChildCell[] = [
    {
        id: 'id',
        numeric: false,
        disablePadding: true,
        label: 'Cross Check ID',
    },
    {
        id: 'uploadStatus',
        numeric: true,
        disablePadding: false,
        label: 'Upload Status',
    },
    {
        id: 'duplicateZip',
        numeric: true,
        disablePadding: false,
        label: 'Zip file name',
    },
    // {
    //     id: 'reWorkExpected',
    //     numeric: true,
    //     disablePadding: false,
    //     label: 'Re-work Expected',
    // },
    // {
    //     id: 'reWorkDetected',
    //     numeric: true,
    //     disablePadding: false,
    //     label: 'Re-work Detected',
    // },
    {
        id: 'duplicateZip',
        numeric: true,
        disablePadding: false,
        label: 'Duplicate Zip',
    },
];
interface EnhancedTableProps {
    onRequestSort: (
        event: React.MouseEvent<unknown>,
        property: keyof Data
    ) => void;
    order: Order;
    orderBy: string;
    dataHistory: any[];
    setDataHistory: React.Dispatch<React.SetStateAction<any[]>>;
    sortOrder: string;
    setSortOrder: React.Dispatch<React.SetStateAction<string>>;
}
interface EnhancedTableChildProps {
    // onRequestSort: (
    //     event: React.MouseEvent<unknown>,
    //     property: keyof PacketData,
    //     parent: string
    // ) => void;
    order: Order;
    orderBy: string;
    // parent: string;
}

interface DropDownProps {
    order: 'asc' | 'desc';
    orderBy: string;
    className: string;
    headCellId: keyof Data | keyof PacketData;
}

const EnhancedTableHead = (props: EnhancedTableProps): React.ReactElement => {
    const {
        order,
        orderBy,
        onRequestSort,
        dataHistory,
        setDataHistory,
        sortOrder,
        setSortOrder,
    } = props;

    const headerTheme = React.useContext(HeaderContext);
    const [rowRef, { width }] = useMeasure<HTMLTableRowElement>();
    const cellRef = React.useRef<HTMLTableCellElement[]>([]);

    React.useLayoutEffect(() => {
        const { current: arrayHeader } = cellRef;
        if (headerTheme) {
            arrayHeader.forEach((el, index) => {
                const layoutEl = el.getBoundingClientRect();
                switch (index) {
                    case 0:
                        headerTheme.first = layoutEl.width - 32;
                        break;
                    case 1:
                        headerTheme.second = layoutEl.width - 32;
                        break;
                    case 2:
                        headerTheme.third = layoutEl.width - 32;
                        break;
                    case 3:
                        headerTheme.fourth = layoutEl.width - 32;
                        break;
                    case 4:
                        headerTheme.fifth = layoutEl.width - 32;
                        break;
                    case 5:
                        headerTheme.sixth = layoutEl.width - 32;
                        break;
                    default:
                        break;
                }
            });
        }
    }, [width]);

    /**
     * Sorts data history based on the specified property.
     * @param {string} property - The property to sort by ('uploadSessionId', 'uploadStatus', 'clientName', 'bin').
     */
    const sortingData = (property: string): void => {
        try {
            /**
             * Sorting functions for different properties.
             * @type {Object.<string, Function>}
             */
            const sortingFunctions: any = {
                actions: (
                    a: { actions: { uuid: any } },
                    b: { actions: { uuid: any } }
                ) => a.actions.uuid.localeCompare(b.actions.uuid),
                uploadStatus: (a: { status: string }, b: { status: any }) =>
                    a.status.localeCompare(b.status),
                clientName: (
                    a: { clientName: string },
                    b: { clientName: any }
                ) => a.clientName.localeCompare(b.clientName),
                bin: (a: { binNumber: string }, b: { binNumber: string }) =>
                    String(a.binNumber).localeCompare(b.binNumber),
            };

            const sortedData = [...dataHistory];
            const prop = property === 'uploadSessionId' ? 'actions' : property;

            sortedData.sort((a, b) =>
                sortOrder === 'asc'
                    ? sortingFunctions[prop](a, b)
                    : sortingFunctions[prop](b, a)
            );

            setDataHistory(sortedData);
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } catch (error) {
            // Intentionally left empty (no action required here)
            // eslint-disable-next-line no-empty
        }
    };

    const actionOnTableSortToSorting =
        (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
            sortingData(property);
            if (property === 'downloadLink' || property === 'packetsData') {
                event.preventDefault();
            } else {
                onRequestSort(event, property);
            }
        };

    const renderDropDownIndicator = (
        _props: DropDownProps
    ): React.ReactElement => {
        const arrowUpSelector =
            order === 'asc' && orderBy === _props.headCellId
                ? 'selected'
                : 'not-selected';
        const arrowDownSelector =
            order === 'desc' && orderBy === _props.headCellId
                ? 'selected'
                : 'not-selected';
        return (
            <Stack
                direction="column"
                className={`table-history-indicator-sort ${_props.className}`}
            >
                <SvgIcon
                    component={TriangleIcon}
                    inheritViewBox
                    className={`table-history-indicator-up ${arrowUpSelector}`}
                    fontSize="inherit"
                />
                <SvgIcon
                    component={TriangleIcon}
                    inheritViewBox
                    className={`table-history-indicator-down ${arrowDownSelector}`}
                    fontSize="inherit"
                />
            </Stack>
        );
    };
    return (
        <TableHead className="history-table-head">
            <TableRow ref={rowRef}>
                {headCells.map((headCell, index) => (
                    <TableCell
                        ref={(ref) => {
                            if (ref) {
                                cellRef.current[index] =
                                    ref as HTMLTableCellElement;
                            }
                        }}
                        key={`${headCell.id} - ${index}`}
                        align="left"
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={
                                !(
                                    headCell.id === 'packetsData' ||
                                    headCell.id === 'downloadLink'
                                )
                            }
                            hideSortIcon
                            onClick={actionOnTableSortToSorting(headCell.id)}
                            IconComponent={(iconProps: any) =>
                                renderDropDownIndicator({
                                    className: iconProps.className,
                                    headCellId: headCell.id,
                                    order,
                                    orderBy,
                                })
                            }
                        >
                            <Box>{headCell.label}</Box>
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc'
                                        ? 'sorted descending'
                                        : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};

const EnhancedTableChildHead = (
    props: EnhancedTableChildProps
): React.ReactElement => {
    const {
        order,
        orderBy,
        // onRequestSort,
        // parent
    } = props;
    // const actionOnChildTableSortToSorting =
    //     (property: keyof PacketData) => (event: React.MouseEvent<unknown>) => {
    //         onRequestSort(event, property, parent);
    //     };
    // const renderDropdownIndicator = (
    //     _props: DropDownProps
    // ): React.ReactElement => {
    //     const arrowUpSelector =
    //         order === 'asc' && orderBy === _props.headCellId
    //             ? 'selected'
    //             : 'not-selected';
    //     const arrowDownSelector =
    //         order === 'desc' && orderBy === _props.headCellId
    //             ? 'selected'
    //             : 'not-selected';
    //     return (
    //         <Stack
    //             direction="column"
    //             className={`table-history-indicator-sort ${_props.className}`}
    //         >
    //             <SvgIcon
    //                 component={TriangleIcon}
    //                 inheritViewBox
    //                 className={`table-history-indicator-up ${arrowUpSelector}`}
    //                 fontSize="inherit"
    //             />
    //             <SvgIcon
    //                 component={TriangleIcon}
    //                 inheritViewBox
    //                 className={`table-history-indicator-down ${arrowDownSelector}`}
    //                 fontSize="inherit"
    //             />
    //         </Stack>
    //     );
    // };
    const [TableCellFirstWidth, setTableCellFirstWidth] =
        React.useState<number>(0);
    const [TableCellSecondWidth, setTableCellSecondWidth] =
        React.useState<number>(0);
    const [TableCellThirdWidth, setTableCellThirdWidth] =
        React.useState<number>(0);
    const [TableCellFourthWidth, setTableCellFourthWidth] =
        React.useState<number>(0);
    const [TableCellFifthWidth, setTableCellFifthWidth] =
        React.useState<number>(0);
    const [TableCellSixthWidth, setTableCellSixthWidth] =
        React.useState<number>(0);
    const [rowRef, { width }] = useMeasure<HTMLTableRowElement>();
    const headerTheme = React.useContext(HeaderContext);

    React.useLayoutEffect(() => {
        if (headerTheme) {
            setTableCellFirstWidth(headerTheme.first);
            setTableCellSecondWidth(headerTheme.second);
            setTableCellThirdWidth(headerTheme.third);
            setTableCellFourthWidth(headerTheme.fourth);
            setTableCellFifthWidth(headerTheme.fifth);
            setTableCellSixthWidth(headerTheme.sixth);
        }
    }, [width]);

    const generateProperTableCellLayout = React.useCallback(
        (index: number) => {
            let widthStringify = '0px';
            switch (index) {
                case 0:
                    widthStringify = `${TableCellFirstWidth}px`;
                    break;
                case 1:
                    widthStringify = `${TableCellSecondWidth}px`;
                    break;
                case 2:
                    widthStringify = `${TableCellThirdWidth}px`;
                    break;
                case 3:
                    widthStringify = `${TableCellFourthWidth}px`;
                    break;
                case 4:
                    widthStringify = `${TableCellFifthWidth}px`;
                    break;
                case 5:
                    widthStringify = `${TableCellSixthWidth}px`;
                    break;

                default:
                    break;
            }
            return widthStringify;
        },
        [
            TableCellFifthWidth,
            TableCellFirstWidth,
            TableCellFourthWidth,
            TableCellSecondWidth,
            TableCellSixthWidth,
            TableCellThirdWidth,
        ]
    );
    React.useEffect(() => {
        window.addEventListener('error', (e) => {
            if (
                e.message ===
                'ResizeObserver loop completed with undelivered notifications.'
            ) {
                const resizeObserverErrDiv = document.getElementById(
                    'webpack-dev-server-client-overlay-div'
                );
                const resizeObserverErr = document.getElementById(
                    'webpack-dev-server-client-overlay'
                );
                if (resizeObserverErr) {
                    resizeObserverErr.setAttribute('style', 'display: none');
                }
                if (resizeObserverErrDiv) {
                    resizeObserverErrDiv.setAttribute('style', 'display: none');
                }
            }
        });
    }, []);

    return (
        <TableHead className="history-table-head child">
            <TableRow ref={rowRef}>
                {headChildCells.map((headCell, index) => (
                    <TableCell
                        sx={{ width: generateProperTableCellLayout(index) }}
                        key={`${headCell.id} - ${index}`}
                        align="left"
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <Box>{headCell.label}</Box>
                        {orderBy === headCell.id ? (
                            <Box component="span" sx={visuallyHidden}>
                                {order === 'desc'
                                    ? 'sorted descending'
                                    : 'sorted ascending'}
                            </Box>
                        ) : null}
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
};
interface ChildOrder {
    order: Order;
    orderBy: keyof PacketData;
    selectedParent: string;
}

type EnhancedTable = {
    uploadHistory: any[];
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    totalRows: number;
    rowsPerPage: number;
    isLoading: any;
    isNetworkError: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
};

export default function EnhancedTable({
    uploadHistory,
    page,
    setPage,
    totalRows,
    isLoading,
    setIsLoading,
    rowsPerPage,
    isNetworkError,
    setRowsPerPage,
}: EnhancedTable): React.ReactElement {
    const [order, setOrder] = React.useState<Order>('desc');
    const [orderChilds, setChildsOrder] = React.useState<readonly ChildOrder[]>(
        []
    );
    const [orderBy, setOrderBy] = React.useState<keyof Data>('uploadSessionId');
    const [selected, setSelected] = React.useState<readonly string[]>([]);
    const [dataHistory, setDataHistory] = React.useState<any[]>(uploadHistory);
    const [sortOrder, setSortOrder] = React.useState<string>('desc');
    const [isDownloading, setDownloading] = React.useState<boolean>(false);
    const [isDownloadingIndex, setDownloadingIndex] = React.useState<
        number | null
    >(null);
    const actionOnTableHeadToRequestSort = (
        event: React.MouseEvent<unknown>,
        property: keyof Data
    ): void => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
        setSelected([]); // reset because no unique id for record.
        setChildsOrder([]);
    };
    // const actionOnChildTableHeadToRequestSort = React.useCallback(
    //     (
    //         event: React.MouseEvent<unknown>,
    //         property: keyof PacketData,
    //         parent: string
    //     ) => {
    //         const selectedIndex = orderChilds.findIndex(
    //             (o) => o.selectedParent === parent
    //         );
    //         if (selectedIndex > -1) {
    //             const isAsc =
    //                 orderChilds[selectedIndex].orderBy === property &&
    //                 orderChilds[selectedIndex].order === 'asc';

    //             orderChilds[selectedIndex].order = isAsc ? 'desc' : 'asc';
    //             orderChilds[selectedIndex].orderBy = property;
    //             setChildsOrder([...orderChilds]);
    //         } else {
    //             const newOrderChilds = orderChilds.concat([
    //                 { selectedParent: parent, order: 'asc', orderBy: property },
    //             ]);
    //             setChildsOrder([...newOrderChilds]);
    //         }
    //     },
    //     [orderChilds]
    // );

    const isLabelIdOnSelectedChildToBoolean = (name: string): boolean =>
        selected.indexOf(name) !== -1;

    /**
     * Handles pagination page change.
     * @param {object} event - The event object.
     * @param {number} newPage - The new page number.
     * @returns {void}
     */
    const onChangePagination = (event: any, newPage: number): void => {
        setIsLoading(true);
        setPage(newPage - 1);
    };

    const handleChangeRowsPerPage = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
            setIsLoading(true);
        },
        [setIsLoading, setPage, setRowsPerPage]
    );

    // refresh upload history data to new state
    React.useEffect(() => {
        setDataHistory(uploadHistory);
    }, [uploadHistory]);

    const initialHeaderStyle = React.useMemo(
        () => ({
            first: 0,
            second: 0,
            third: 0,
            fourth: 0,
            fifth: 0,
            sixth: 0,
        }),
        []
    );
    /**
     * Downloads an array of files based on the provided URLs.
     *
     * @param arrayOfObjects - "file" property with the URL.
     * @returns {Promise<void>} A promise that resolves when all files are downloaded.
     */
    // const downloadFileOnSaS = async (fileOnSaS: string): Promise<void> => {
    //     setDownloading(true);
    //     try {
    //         const fileName: any = fileOnSaS.split('/').pop();
    //         await downloadFile(fileOnSaS, fileName);

    //         setDownloading(false);
    //     } catch (error) {
    //         setDownloading(false);
    //         Toast.showErrorMessage('Error when download file');
    //     }
    // };

    const handleAuditFilename = React.useCallback(
        (url: URL, type: string) =>
            url.pathname?.split('/').find((segment) => segment.includes(type)),
        []
    );
    const handleAuditCSVFilename = React.useCallback(
        (filename: string, type: string) =>
            filename?.split('/').find((segment) => segment.includes(type)),
        []
    );
    /**
     * Downloads an Zips based on the multiple files.
     *
     * @param arrayOfPendingPDF - "file" url in array.
     * @param arrayOfPendingCSV - "file" url in array.
     * @param packetId - "packetId" in string
     * @returns {Promise<void>} A promise that resolves when all files are downloaded.
     */
    const DownloadAuditZip = async (
        pendingPDF: any,
        pendingCSV: any,
        index: number
    ): Promise<void> => {
        setDownloading(true);
        setDownloadingIndex(index);
        const placeholderPDF = handleAuditFilename(new URL(pendingPDF), '.pdf');
        const placeholderCSV = handleAuditCSVFilename(pendingCSV, '.csv');

        try {
            let pendingCSVURL = null;
            if (pendingCSV) {
                const sasCSVURL = await ManagePacketService.getSasToken({
                    fileName: pendingCSV,
                    report: true,
                }).catch((error: any) => {
                    throw error;
                });
                pendingCSVURL = sasCSVURL.data.data.url;
            }

            const pendingBlobFromDocs: Blob[] = await Promise.all<Blob>(
                pendingCSVURL
                    ? [downloadPDF(pendingPDF), downloadCSV(pendingCSVURL)]
                    : [downloadPDF(pendingPDF)]
            ).catch((error: any) => {
                throw error;
            });

            // create new zip when there is processedFile
            const zip = new JSZip();

            pendingBlobFromDocs.forEach((blob, i) => {
                const placeholder = i === 0 ? placeholderPDF : placeholderCSV;
                zip.file(placeholder ?? '', blob);
            });

            zip.generateAsync({ type: 'blob' }).then((content) => {
                // see FileSaver.js
                const placeholderZip = placeholderPDF
                    ? placeholderPDF.replace('.pdf', '.zip')
                    : 'audit.zip';
                setDownloading(false);
                setDownloadingIndex(null);
                FileSaver.saveAs(content, placeholderZip);
            });
            // setDownloadedZip(true);
        } catch (error: any) {
            setDownloading(false);
            setDownloadingIndex(null);
            if (
                error !== null &&
                error instanceof AxiosError &&
                error.code === 'ERR_NETWORK'
            ) {
                Toast.showErrorMessage(
                    'No internet connection, Please try again!'
                );
            } else {
                Utils.handleErrorResponse(error);
            }
        }
    };

    return (
        <Box className="table-history-container">
            {isLoading && (
                <Box className="align-loader">
                    <CircularProgress
                        className="mr-2 margin-loader"
                        size={54}
                        color="inherit"
                        title="progress loading"
                    />
                </Box>
            )}
            {!isLoading && dataHistory.length > 0 && (
                <HeaderContext.Provider value={initialHeaderStyle}>
                    <Paper elevation={0} className="history-table">
                        <TableContainer className="table-history-tablecontainer">
                            <Table aria-labelledby="tableTitle" size="medium">
                                <EnhancedTableHead
                                    order={order}
                                    orderBy={orderBy}
                                    onRequestSort={
                                        actionOnTableHeadToRequestSort
                                    }
                                    dataHistory={dataHistory}
                                    setDataHistory={setDataHistory}
                                    sortOrder={sortOrder}
                                    setSortOrder={setSortOrder}
                                />
                                <TableBody className="history-table-body">
                                    {dataHistory.map(
                                        (row: any, index: number) => {
                                            const labelId = `enhanced-table-checkbox-${index}-${row.actions.uuid}-${page}`;
                                            const isItemSelected =
                                                isLabelIdOnSelectedChildToBoolean(
                                                    labelId
                                                );
                                            const selectedIndex =
                                                orderChilds.findIndex(
                                                    (o) =>
                                                        o.selectedParent ===
                                                        labelId
                                                );
                                            const contextSelected =
                                                isItemSelected
                                                    ? 'Hide'
                                                    : 'Show';
                                            return (
                                                <React.Fragment
                                                    key={`${row.binNumber} - ${index}`}
                                                >
                                                    <TableRow
                                                        key={`${row.binNumber} - ${index}`}
                                                    >
                                                        <TableCell
                                                            component="th"
                                                            id={labelId}
                                                            scope="row"
                                                        >
                                                            {row.actions.uuid}
                                                        </TableCell>
                                                        <TableCell
                                                            align="left"
                                                            className="table-history-capitalize"
                                                        >
                                                            {row.status}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {row.clientName}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            {row.binNumber}
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            <Button
                                                                disableRipple
                                                                variant="text"
                                                                disabled={
                                                                    (isDownloading &&
                                                                        typeof isDownloadingIndex ===
                                                                            'number' &&
                                                                        isDownloadingIndex ===
                                                                            index) ||
                                                                    row.auditFile ===
                                                                        null ||
                                                                    String(
                                                                        row.status
                                                                    )
                                                                        .toLowerCase()
                                                                        .includes(
                                                                            'pending'
                                                                        ) ===
                                                                        true
                                                                }
                                                                onClick={() =>
                                                                    DownloadAuditZip(
                                                                        row.auditFile,
                                                                        row.csvAuditFile,
                                                                        index
                                                                    )
                                                                }
                                                            >
                                                                Download Audit
                                                            </Button>
                                                        </TableCell>
                                                        <TableCell align="left">
                                                            <Link
                                                                className="link-more-details"
                                                                href={`#${labelId}`}
                                                                underline="always"
                                                                aria-expanded={
                                                                    isItemSelected
                                                                }
                                                                role="button"
                                                                aria-controls="panel-detail-history"
                                                                id="link-to-detail-history"
                                                                onClick={() => {
                                                                    if (
                                                                        isItemSelected
                                                                    ) {
                                                                        setSelected(
                                                                            selected.filter(
                                                                                (
                                                                                    popul
                                                                                ) =>
                                                                                    popul !==
                                                                                    labelId
                                                                            )
                                                                        );
                                                                    } else {
                                                                        setSelected(
                                                                            selected.concat(
                                                                                [
                                                                                    labelId,
                                                                                ]
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                            >{`${contextSelected} details`}</Link>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow className="packets-row">
                                                        <TableCell
                                                            className="packets"
                                                            colSpan={9}
                                                        >
                                                            <Collapse
                                                                in={
                                                                    isItemSelected
                                                                }
                                                                timeout="auto"
                                                                unmountOnExit
                                                                role="region"
                                                                id="panel-detail-history"
                                                                aria-labelledby="link-to-detail-history"
                                                            >
                                                                <Table
                                                                    className="table-packets"
                                                                    size="small"
                                                                    aria-label="purchases"
                                                                >
                                                                    <EnhancedTableChildHead
                                                                        // parent={
                                                                        //     labelId
                                                                        // }
                                                                        order={
                                                                            orderChilds &&
                                                                            selectedIndex >
                                                                                -1
                                                                                ? orderChilds[
                                                                                      selectedIndex
                                                                                  ]
                                                                                      .order
                                                                                : 'asc'
                                                                        }
                                                                        orderBy={
                                                                            orderChilds &&
                                                                            selectedIndex >
                                                                                -1
                                                                                ? orderChilds[
                                                                                      selectedIndex
                                                                                  ]
                                                                                      .orderBy
                                                                                : 'id'
                                                                        }
                                                                        // onRequestSort={
                                                                        //     actionOnChildTableHeadToRequestSort
                                                                        // }
                                                                    />

                                                                    <TableBody>
                                                                        {row.packets.map(
                                                                            (
                                                                                packetsRow: any,
                                                                                i: number
                                                                            ) => (
                                                                                <TableRow
                                                                                    key={`${packetsRow.actions.uuid} - ${i}`}
                                                                                >
                                                                                    <TableCell
                                                                                        component="th"
                                                                                        scope="row"
                                                                                    >
                                                                                        {
                                                                                            packetsRow
                                                                                                .actions
                                                                                                .uuid
                                                                                        }
                                                                                    </TableCell>
                                                                                    <TableCell
                                                                                        align="left"
                                                                                        className="table-history-capitalize"
                                                                                    >
                                                                                        {
                                                                                            packetsRow.uploadStatus
                                                                                        }
                                                                                    </TableCell>
                                                                                    <TableCell align="left">
                                                                                        {
                                                                                            packetsRow.placeholder
                                                                                        }
                                                                                    </TableCell>
                                                                                    <TableCell align="left">
                                                                                        {packetsRow.duplicateZip
                                                                                            ? 'Yes'
                                                                                            : 'No'}
                                                                                    </TableCell>
                                                                                    {/* <TableCell align="left">
                                                                                            {packetsRow.reworkDetected
                                                                                                ? 'Yes'
                                                                                                : 'No'}
                                                                                        </TableCell>
                                                                                        <TableCell align="left">
                                                                                            {packetsRow.reworkExpected
                                                                                                ? 'Yes'
                                                                                                : 'No'}
                                                                                        </TableCell> */}
                                                                                </TableRow>
                                                                            )
                                                                        )}
                                                                    </TableBody>
                                                                </Table>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        }
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </HeaderContext.Provider>
            )}

            {!isLoading &&
                dataHistory.length < 1 &&
                isNetworkError === false && (
                    <EmptyPlaceholder message="There are no uploaded records yet" />
                )}

            {!isLoading && dataHistory.length < 1 && isNetworkError && (
                <EmptyPlaceholder message="No internet connection, Please try again!" />
            )}
            {!isLoading && dataHistory.length >= 1 && (
                <Box className="table-history-padding-pagination">
                    <FlatPaginationComp
                        containerClassName="historytable"
                        className="table-history-pagination-mc"
                        data={totalRows}
                        page={page}
                        onChange={onChangePagination}
                        popupPrevNext
                        hidden={false}
                        count={Math.ceil(totalRows / rowsPerPage)}
                        // eslint-disable-next-line func-names, react/jsx-no-bind
                        onPageChange={onChangePagination}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Box>
            )}
        </Box>
    );
}
